/**
 * Urine Test Formula
 * ตรวจสอบผลการตรวจปัสสาวะ
 *
 * ใช้ logic เดียวกับ mfu-admin compareValues:
 * - normalRange เป็น text → split by comma → เทียบ lowercase (strip whitespace)
 * - normalRange เป็นตัวเลข → เทียบ range เช่น "0-2", "<1.030"
 */

/**
 * เปรียบเทียบค่าจริงกับ normal range (ported จาก healthbook.js compareValues)
 * @param {string} normalRange - ค่าปกติ เช่น "Negative,Neg", "0-2", "<1.030"
 * @param {string} resultValue - ค่าที่ตรวจได้
 * @returns {boolean} true = ปกติ
 */
const isNormal = (normalRange, resultValue) => {
  if (!normalRange || !resultValue) return true;

  const rv = resultValue.toString().trim();
  if (rv === '-' || rv === '') return true;

  // ถ้า normalRange เป็น text (มีตัวอักษร แต่ไม่ใช่ gender format "M x-y, F x-y")
  const normalRangeIsGenderFormat = /^[MF]\s/.test(normalRange.trim());
  if (/[a-zA-Z]/.test(normalRange.trim()) && !normalRangeIsGenderFormat) {
    const normalizedResult = rv.toLowerCase().replace(/\s+/g, '');
    const normalValues = normalRange
      .split(',')
      .map(v => v.trim().toLowerCase().replace(/\s+/g, ''));
    return normalValues.includes(normalizedResult);
  }

  // ตัวเลข
  const result = parseFloat(rv);
  if (isNaN(result)) return true;

  if (normalRange.includes('-')) {
    const [min, max] = normalRange.split('-').map(v => parseFloat(v.trim()));
    return result >= min && result <= max;
  } else if (normalRange.startsWith('<')) {
    const max = parseFloat(normalRange.replace('<', '').trim());
    return result < max;
  } else if (normalRange.startsWith('>')) {
    const min = parseFloat(normalRange.replace('>', '').trim());
    return result > min;
  }

  return true;
};

// normal ranges สำหรับแต่ละ field (ตรงกับ admin)
const URINE_NORMAL_RANGES = {
  clarity:         'Clear',
  ph:              '4.5-8.0',
  specificGravity: '1.003-1.030',
  protein:         'Negative,Neg,Trace',
  glucose:         'Negative,Neg',
  ketones:         'Negative,Neg',
  blood:           'Negative,Neg',
  bilirubin:       'Negative,Neg',
  urobilinogen:    'Normal,Negative,Neg,0.1-1.0',
  leukocytes:      'Negative,Neg',
  nitrite:         'Negative,Neg',
  redBloodCells:   '0-2',
  whiteBloodCells: '0-5',
  epithelialCells: '0-5',
  bacteria:        'Negative,Neg,None',
};

const FIELD_LABEL = {
  clarity:         'Appearance',
  ph:              'pH',
  specificGravity: 'Specific Gravity',
  protein:         'Protein',
  glucose:         'Glucose',
  ketones:         'Ketones',
  blood:           'Blood',
  bilirubin:       'Bilirubin',
  urobilinogen:    'Urobilinogen',
  leukocytes:      'Leukocytes',
  nitrite:         'Nitrite',
  redBloodCells:   'Red Blood Cells',
  whiteBloodCells: 'White Blood Cells',
  epithelialCells: 'Epithelial Cells',
  bacteria:        'Bacteria',
};

const UrineTestFormula = (urineData) => {
  if (!urineData || typeof urineData !== 'object') {
    return {
      status: 'ไม่มีข้อมูล',
      color: 'gray',
      description: 'ไม่พบข้อมูลการตรวจปัสสาวะ',
      abnormalFindings: [],
    };
  }

  const abnormalFindings = [];

  Object.entries(URINE_NORMAL_RANGES).forEach(([field, normalRange]) => {
    const value = urineData[field];
    if (value && !isNormal(normalRange, value)) {
      abnormalFindings.push(FIELD_LABEL[field]);
    }
  });

  if (abnormalFindings.length === 0) {
    return {
      status: 'ปกติ',
      color: 'green',
      description: 'ผลการตรวจปัสสาวะอยู่ในเกณฑ์ปกติ',
      abnormalFindings: [],
    };
  }

  return {
    status: 'ผิดปกติ',
    color: 'red',
    description: `พบค่าผิดปกติ: ${abnormalFindings.join(', ')}`,
    abnormalFindings,
  };
};

export default UrineTestFormula;
